// Setup › Finance › Categories (`/c/{slug}/setup/finance/categories`; 11 §Planning "Exports",
// M5-03 `categories.*`): the chart of accounts the exports map rows to. An expense category lists
// the cost kinds it takes (a ledger or roll-up row goes to the active expense category holding its
// kind, lowest code first); revenue rows name their income category. Managers read; finance admins
// add, rename, re-map and archive. Calls on load: the standing and the categories.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArchiveIcon, ArchiveRestoreIcon, BookOpenIcon, PencilIcon, PlusIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { ConfirmRowAction } from "../confirm-row-action";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { RowAction, RowActions } from "../settings/row-actions";
import {
  FINANCE_KIND_LABELS,
  FinancePage,
  FormDialog,
  FormField,
  ReadOnlyNotice,
  useFinanceStanding,
} from "./finance-parts";
import { categoriesQuery, financeKeys } from "./finance-queries";

type Category = Awaited<ReturnType<Api["client"]["categories"]["list"]>>["data"][number];
type Kind = Category["kind"];

/** The kinds an expense category can take: the ledger's and the accrued ones. */
export const MAPPABLE_KINDS = [
  "de_subscription",
  "de_usage",
  "agent_tokens",
  "agent_compute",
  "agent_tool",
  "human_time",
  "adjustment",
  "human_comp",
  "benefit",
  "overhead",
  "software_subscription",
] as const;

export function CategoriesScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const standing = useFinanceStanding(api, company);
  const list = useInfiniteQuery({ ...categoriesQuery(api, company.id), enabled: standing.loaded });
  const [editing, setEditing] = useState<Category | "new" | null>(null);
  const qc = useQueryClient();
  const archive = useMutation({
    mutationFn: (c: Category) =>
      api.client.categories.update({
        params: { co: company.id, id: c.id },
        body: { status: c.status === "active" ? "archived" : "active" },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: financeKeys.all(company.id) }),
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  return (
    <FinancePage
      company={company}
      where="setup"
      title="Categories"
      icon={BookOpenIcon}
      actions={
        standing.admin ? (
          <Button size="sm" onClick={() => setEditing("new")}>
            <PlusIcon aria-hidden="true" />
            Add a category
          </Button>
        ) : null
      }
    >
      {standing.query.isPending || list.isPending ? (
        <Loading label="Loading categories" rows={3} />
      ) : standing.query.isError ? (
        <LoadError what="Your finance access" error={standing.query.error} />
      ) : list.isError ? (
        <LoadError what="Categories" error={list.error} />
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          {!standing.admin ? <ReadOnlyNotice company={company} owner={standing.owner} /> : null}
          {rows.length === 0 ? (
            <EmptyState
              variant="card"
              icon={BookOpenIcon}
              title="No categories yet"
              description="Add your accounts (with their codes) so exports land in your books as they are."
            />
          ) : (
            <>
              <Table data-slot="categories">
                <TableCaption>
                  A kind in two expense categories goes to the lower code.
                </TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Code</TableHead>
                    <TableHead scope="col">Category</TableHead>
                    <TableHead scope="col">Type</TableHead>
                    <TableHead scope="col" className="max-md:hidden">
                      Takes
                    </TableHead>
                    <TableHead scope="col">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((c) => (
                    <TableRow key={c.id} className="group/row" data-category={c.id}>
                      <TableCell className="tabular-nums text-muted-foreground">
                        {c.code ?? "—"}
                      </TableCell>
                      <TableHead scope="row" className="font-normal">
                        {c.name}
                        {c.status === "archived" ? (
                          <Badge variant="neutral" className="ml-2">
                            Archived
                          </Badge>
                        ) : null}
                      </TableHead>
                      <TableCell>
                        <Badge variant={c.kind === "income" ? "success" : "neutral"}>
                          {c.kind === "income" ? "Income" : "Expense"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-md:hidden">
                        {c.cost_kinds.length
                          ? c.cost_kinds.map((k) => FINANCE_KIND_LABELS[k] ?? k).join(", ")
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {standing.admin ? (
                          <RowActions>
                            <RowAction
                              icon={PencilIcon}
                              label={`Edit ${c.name}`}
                              tip="Edit"
                              onClick={() => setEditing(c)}
                            />
                            {c.status === "active" ? (
                              <ConfirmRowAction
                                icon={ArchiveIcon}
                                label={`Archive ${c.name}`}
                                tip="Archive"
                                disabled={archive.isPending}
                                title={`Archive “${c.name}”?`}
                                description="Exports stop mapping rows to it. Restore it any time."
                                confirmLabel="Archive"
                                onConfirm={() => archive.mutate(c)}
                              />
                            ) : (
                              <RowAction
                                icon={ArchiveRestoreIcon}
                                label={`Restore ${c.name}`}
                                tip="Restore"
                                disabled={archive.isPending}
                                onClick={() => archive.mutate(c)}
                              />
                            )}
                          </RowActions>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <LoadMore
                what="categories"
                hasMore={list.hasNextPage}
                loading={list.isFetchingNextPage}
                onMore={() => void list.fetchNextPage()}
              />
            </>
          )}
        </div>
      )}
      {editing ? (
        <CategoryDialog
          api={api}
          company={company}
          row={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </FinancePage>
  );
}

function KindCheck({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-center gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      <Label htmlFor={id}>{label}</Label>
    </div>
  );
}

function CategoryDialog({
  api,
  company,
  row,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  row: Category | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(row?.name ?? "");
  const [code, setCode] = useState(row?.code ?? "");
  const [kind, setKind] = useState<Kind>(row?.kind ?? "expense");
  const [kinds, setKinds] = useState<Set<string>>(new Set(row?.cost_kinds ?? []));
  const ids = { name: useId(), code: useId(), kind: useId(), kinds: useId() };
  const body = {
    name: name.trim(),
    code: code.trim() || null,
    cost_kinds: kind === "expense" ? [...kinds] : [],
  };
  const save = useMutation({
    mutationFn: () =>
      row
        ? api.client.categories.update({ params: { co: company.id, id: row.id }, body })
        : api.client.categories.create({
            params: { co: company.id },
            idempotencyKey: newIdempotencyKey(),
            body: { ...body, kind },
          }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: financeKeys.all(company.id) });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title={row ? "Edit category" : "Add a category"}
      submitLabel={row ? "Save" : "Add category"}
      pending={save.isPending}
      error={save.error}
      canSubmit={name.trim().length > 0}
      onSubmit={() => save.mutate()}
    >
      <div className="grid gap-4 sm:grid-cols-3">
        <FormField id={ids.code} label="Code">
          <Input
            id={ids.code}
            maxLength={64}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </FormField>
        <div className="sm:col-span-2">
          <FormField id={ids.name} label="Name">
            <Input
              id={ids.name}
              required
              maxLength={200}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </FormField>
        </div>
      </div>
      {row ? null : (
        <FormField id={ids.kind} label="Type">
          <NativeSelect
            id={ids.kind}
            value={kind}
            onChange={(e) => setKind(e.target.value as Kind)}
          >
            <NativeSelectOption value="expense">Expense</NativeSelectOption>
            <NativeSelectOption value="income">Income</NativeSelectOption>
          </NativeSelect>
        </FormField>
      )}
      {(row?.kind ?? kind) === "expense" ? (
        <fieldset className="flex flex-col gap-2" aria-describedby={ids.kinds}>
          <legend className="mb-1 text-sm font-medium">Takes these costs</legend>
          <p id={ids.kinds} className="text-xs text-muted-foreground">
            Rows of these kinds land in this category in exports.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {MAPPABLE_KINDS.map((k) => (
              <KindCheck
                key={k}
                label={FINANCE_KIND_LABELS[k] ?? k}
                checked={kinds.has(k)}
                onChange={(on) =>
                  setKinds((prev) => {
                    const next = new Set(prev);
                    if (on) next.add(k);
                    else next.delete(k);
                    return next;
                  })
                }
              />
            ))}
          </div>
        </fieldset>
      ) : null}
    </FormDialog>
  );
}
